import React, { type FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Skeleton,
  StoreCard as StoreCardAUI,
  Text,
  colors,
} from '@soluto-private/mx-asurion-ui-react';

import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { type ParsedStore } from '@service-initiation/tech-care-data-access-store';
import { formatNextAvailability } from '@service-initiation/tech-care-utils-date-time';

const TEXT = TranslationEntries.Repairs.StoreLocator.StoreCard;
const NEXT_AVAILABILITY_TEXT =
  TranslationEntries.Repairs.DateAndTime.NextAvailabilityFormat;

interface Props {
  focusedStore?: ParsedStore;
  handleStoreClick: (selectedStore: ParsedStore) => void;
  store: ParsedStore;
  storeIndex?: number;
  style?: React.CSSProperties;
}

export const StoreCard: FC<Props> = ({
  focusedStore,
  handleStoreClick,
  store,
  storeIndex,
  style,
}: Props) => {
  const storesContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const nextAvailabilityText = useMemo(
    () =>
      formatNextAvailability(t, store.nextAvailability, NEXT_AVAILABILITY_TEXT),
    [store.nextAvailability, t]
  );

  useEffect(() => {
    const cardElement = storesContainerRef.current;
    if (focusedStore && focusedStore.id === store.id && cardElement) {
      cardElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [store, focusedStore]);

  return (
    <div
      data-testid={`store-${store.id}`}
      onClick={() => handleStoreClick(store)}
      ref={storesContainerRef}
    >
      <StoreCardAUI
        address={store.address}
        badgeColor="primary"
        openHours={
          <>
            <Text size={1}>{t(TEXT.NextAvailability)} </Text>
            {store.nextAvailability ? (
              <Text color={colors.brandGreenDeeper} size={1} weight="heavy">
                {nextAvailabilityText}
              </Text>
            ) : (
              <Skeleton elementCount={1} height="1rem" width="10rem" />
            )}
          </>
        }
        selected={store.id === focusedStore?.id}
        showBadge={!!storeIndex}
        showChevron={true}
        storeName={store.name}
        storeNumber={storeIndex}
        style={{ width: '100%', ...style }}
        type="call-to-action"
      />
    </div>
  );
};
